import { formatDistanceToNow } from 'date-fns';

/**
 * Get relative time string (e.g., "5 minutes ago")
 * @param timestamp - ISO date string or Date object
 * @returns Relative time string with suffix
 */
export function getTimeAgo(timestamp: string | Date): string {
  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return 'unknown';
  }
}

/**
 * Format time string for display
 * @param timeString - ISO time string
 * @returns Formatted locale time string
 */
export function formatTime(timeString: string): string {
  try {
    return new Date(timeString).toLocaleString();
  } catch {
    return timeString;
  }
}

/**
 * Calculate duration between start and end times
 * @param startsAt - Start time ISO string
 * @param endsAt - End time ISO string (optional, uses current time if not provided)
 * @returns Human-readable duration string
 */
export function calculateDuration(startsAt: string, endsAt?: string): string {
  try {
    const start = new Date(startsAt);
    const end = endsAt ? new Date(endsAt) : new Date();
    const duration = end.getTime() - start.getTime();

    const days = Math.floor(duration / (1000 * 60 * 60 * 24));
    const hours = Math.floor((duration % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days}d ${hours}h`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  } catch {
    return 'unknown';
  }
}

/**
 * Format a date for compact display (e.g., in tables)
 * @param dateString - ISO date string
 * @returns Compact formatted date string
 */
export function formatCompactDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch {
    return dateString;
  }
}
